/**
 * A row of choices where more than one can be held at once.
 *
 * The sibling of `StyledRadioGroup`, and on screen the same control: the same
 * bordered tiles, the same gap, the same solid blue with white text when
 * chosen. A tick box in front of the label was the one difference, and it read
 * as a different, shabbier control sitting under two rows of tiles — so the
 * question's own label ("Pick all that apply") carries the news that more than
 * one answer fits, and the tiles carry nothing but the answers.
 *
 * The checkbox is still a checkbox underneath: a real `input type="checkbox"`
 * per tile, visually hidden, so the keyboard reaches it with Tab and Space and
 * a screen reader announces it as a checkbox rather than as one of a set of
 * radios. Its own styles rather than the global `label.button` rules, which
 * are written for antd's radio wrapper and do not reach ours.
 */

import type { CSSProperties } from "react";
import styled from "styled-components";

const BLUE = "var(--blue-300)";
const BORDER = "#e2e2e2";

const Field = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const Choices = styled.div<{ $large: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
  gap: 1rem;
  margin-top: ${(p) => (p.$large ? "8px" : "1px")};
  margin-bottom: 1rem;

  @media (max-width: 500px) {
    gap: 0.5rem;
  }
`;

const Choice = styled.label<{ $large: boolean; $checked: boolean }>`
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 0;
  padding: ${(p) => (p.$large ? "1rem" : "0.45rem")};
  border: 1px solid ${(p) => (p.$checked ? BLUE : BORDER)};
  border-radius: 6px;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.05);
  background: ${(p) => (p.$checked ? BLUE : "#fff")};
  color: ${(p) => (p.$checked ? "#fff" : "inherit")};
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 140ms ease, background-color 140ms ease,
    color 140ms ease;

  /* The real control, kept for the keyboard and the screen reader. */
  input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  &:hover {
    border-color: ${(p) => (p.$checked ? BLUE : "#c6ccff")};
  }

  /* The ring belongs to the keyboard. A click already says where it landed,
     and antd's radio tiles next door do not ring on one. */
  &:has(input:focus-visible) {
    outline: 2px solid ${BLUE};
    outline-offset: 2px;
  }

  @supports not selector(:has(*)) {
    &:focus-within {
      outline: 2px solid ${BLUE};
      outline-offset: 2px;
    }
  }

  @media (max-width: 500px) {
    padding: 0.5rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export function ChoiceGroup({
  id,
  label,
  options,
  value,
  size,
  style,
  onChange,
}: {
  id: string;
  label?: string;
  options: { label: string; value: string }[];
  /** The chosen values. */
  value: string[];
  size?: "large" | "middle" | "small";
  /** Laid over the row of tiles, as `StyledRadioGroup` takes one. */
  style?: CSSProperties;
  /** One value was clicked; the caller decides what the selection becomes. */
  onChange: (value: string) => void;
}) {
  const large = size === "large";

  return (
    <Field id={id}>
      {label ? (
        <label htmlFor={id} className="mt-2">
          {label}
        </label>
      ) : null}
      <Choices $large={large} style={style}>
        {options.map((option) => {
          const checked = value.includes(option.value);

          return (
            <Choice key={option.value} $large={large} $checked={checked}>
              <input
                type="checkbox"
                name={id}
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
              />
              {option.label}
            </Choice>
          );
        })}
      </Choices>
    </Field>
  );
}
